<template>
    <div class="mms-filter">
        <!-- Titlen tegnes af indpakningen, som også bærer sammenklapningen. -->
        <div class="mms-filter__row">
            <!--
                IKKE disabled.

                Den var låst indtil man havde valgt en værdi, så man skulle
                vælge tallet FØRST mens den stod på "=", og først bagefter
                kunne man skifte til Min. Rækkefølgen er omvendt af hvordan man
                tænker: "mindst 6 TB", ikke "6 TB, og forresten mindst".

                Værre endnu udløste værdivalget en søgning på lig-med med det
                samme, og et eksakt tal rammer sjældent noget. Så så filteret
                ud til at give nul varer.
            -->
            <select v-model="selectedType" class="mms-filter__select mms-filter__select--type">
                <option value="equal">=</option>
                <option value="min">Min</option>
                <option value="max">Max</option>
            </select>
            <!--
                Skriv for at finde værdien.
                Listen indeholder ALLE værdier der findes i kategorien, og den
                kan være lang: HDD kapacitet har 49 og bliver til flere når
                filter:sweep har kørt. En rulleliste med halvtreds kapaciteter
                er ikke til at ramme i.

                Fri indtastning ville ikke give mere: "min 6 TB" rammer præcis
                de samme varer som "min" på den mindste listede værdi over
                6 TB, for listen ER kataloget. Det der manglede var at kunne
                FINDE den værdi.
            -->
            <div class="mms-filter__vaelger">
                <input
                    v-if="brugSoegning"
                    v-model="soegning"
                    type="search"
                    class="mms-filter__soeg"
                    :placeholder="valgtEtiket || `Søg blandt ${filter.values.length}`"
                    :aria-label="`Søg værdi i ${filter.translation_string_ident}`"
                    @focus="listeAaben = true"
                    @blur="lukSenere"
                />
                <select
                    v-model="selectedValue"
                    class="mms-filter__select mms-filter__select--value"
                    :size="brugSoegning && listeAaben ? Math.min(8, synligeVaerdier.length + 1) : undefined"
                    :class="{ 'mms-filter__select--aaben': brugSoegning && listeAaben }"
                    @change="listeAaben = false"
                >
                    <option value="">Ikke valgt</option>
                    <option v-for="option in synligeVaerdier" :key="option.internal_value" :value="option.internal_value">
                        {{ option.displayed_value }}
                    </option>
                </select>
                <p v-if="brugSoegning && soegning && !synligeVaerdier.length" class="mms-filter__tomt">
                    Ingen værdi hedder noget med "{{ soegning }}".
                </p>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, watch, computed } from 'vue';

const emit = defineEmits(['filterUpdated']);
const props = defineProps<{
    filter: any,
    urlParams?: URLSearchParams,
    categoryName?: string
}>();

function getUrlParamType() {
    // Standard er MIN, ikke lig-med.
    //
    // Et talspec rammes sjældent eksakt: "6 TB" findes måske slet ikke, mens
    // "mindst 6 TB" altid giver mening. Det er også sådan spørgsmålet stilles
    // i forumtrådene, og min/max er dén funktion målgruppen kom efter.
    if (!props.urlParams) return 'min';
    const type = props.urlParams.get(`${props.filter.id}_type`);
    return type || 'min';
}
function getUrlParamValue() {
    if (!props.urlParams) return '';
    const value = props.urlParams.get(`${props.filter.id}_value`);
    return value ?? '';
}

const selectedType = ref(getUrlParamType());
const selectedValue = ref(getUrlParamValue());

/*
 * Fra hvor mange værdier er en rulleliste ikke længere til at ramme i.
 * HDD kapacitet har 49 værdier i dag, og filter:sweep laver 416 nye min/max.
 */
const SOEG_FRA = 12;

const soegning = ref('');
const listeAaben = ref(false);

const brugSoegning = computed(() => (props.filter?.values?.length || 0) > SOEG_FRA);

const synligeVaerdier = computed(() => {
    const alle = props.filter?.values || [];
    const s = soegning.value.trim().toLowerCase();

    if (!s) return alle;

    return alle.filter((v: any) => String(v.displayed_value ?? '').toLowerCase().includes(s));
});

/** Etiketten for den valgte værdi, så feltet viser hvad der er valgt. */
const valgtEtiket = computed(() => {
    const valgt = (props.filter?.values || []).find(
        (v: any) => String(v.internal_value) === String(selectedValue.value),
    );

    return valgt ? String(valgt.displayed_value) : '';
});

/**
 * Luk listen efter et øjeblik.
 *
 * Klikker man på en værdi, udløses blur FØR change. Lukker vi med det samme,
 * forsvinder listen under fingeren og valget når aldrig frem.
 */
function lukSenere() {
    setTimeout(() => { listeAaben.value = false; }, 150);
}


watch(
    () => props.urlParams ? [props.urlParams.get(String(props.filter.id))] : [null],
    ([filterValue]) => {
        if (filterValue && filterValue.includes(';')) {
            const [type, value] = filterValue.split(';');
            if (type !== selectedType.value) selectedType.value = type;
            if (value !== selectedValue.value) selectedValue.value = value;
        } else {
            // Ryd værdien, men lad brugerens valg af min/max/lig-med stå.
            if (selectedValue.value !== '') selectedValue.value = '';
        }
    },
    { immediate: true }
);

function emitAllFilters() {
    const allActiveFilters: any[] = [];
    if (props.urlParams) {
        for (const [key, value] of props.urlParams.entries()) {
            if (key.endsWith('_type') || key.endsWith('_value') || key === 'sort' || key === 'page') continue;
            if (value && value.trim() !== '' && key !== String(props.filter.id)) {
                allActiveFilters.push({ id: key, value });
            }
        }
    }
    if (selectedValue.value !== '') {
        allActiveFilters.push({ id: String(props.filter.id), value: `${selectedType.value};${selectedValue.value}` });
    }
    emit('filterUpdated', allActiveFilters);
}

watch([selectedType, selectedValue], () => {
    /*
     * Typen nulstilles IKKE når værdien er tom.
     *
     * Den gjorde det før, og sammen med at vælgeren var disabled gjorde det
     * min/max ubrugelig: valgte man "Min" inden man havde valgt et tal, blev
     * den øjeblikkeligt sat tilbage til "=". Filteret så låst ud, og det er
     * dén funktion målgruppen kom efter.
     *
     * Rækkefølgen skal være fri: man tænker "mindst 6 TB", ikke "6 TB, og
     * forresten mindst".
     */
    emitAllFilters();
});
</script>

<style scoped>
.mms-filter__vaelger {
    position: relative;
    flex: 1 1 auto;
    min-width: 0;
}

.mms-filter__soeg {
    width: 100%;
    margin-bottom: 4px;
    padding: 4px 8px;
    background: var(--pc-bg-tertiary);
    border: 1px solid var(--pc-border);
    border-radius: var(--pc-radius, 0.375rem);
    color: var(--pc-text-primary);
    font-size: var(--pc-text-sm, 0.875rem);
}

.mms-filter__select--aaben {
    overflow-y: auto;
}

.mms-filter__tomt {
    margin: 4px 0 0;
    font-size: var(--pc-text-xs, 0.75rem);
    color: var(--pc-text-muted, #8a8a8a);
}

.mms-filter__title {
    font-size: 0.95rem;
    font-weight: 600;
    color: var(--pc-text-primary);
    margin-bottom: 0.5rem;
    text-transform: capitalize;
}

.mms-filter__row {
    display: flex;
    gap: 0.5rem;
}

.mms-filter__select {
    padding: 0.4rem 0.6rem;
    font-size: var(--pc-text-sm);
    font-weight: 500;
    color: var(--pc-text-primary);
    background-color: var(--pc-bg-primary);
    border: 1px solid var(--pc-border);
    border-radius: var(--pc-radius-sm);
    outline: none;
    cursor: pointer;
    transition: border-color var(--pc-transition);
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' fill='%238B92A5' viewBox='0 0 16 16'%3E%3Cpath d='M7.247 11.14 2.451 5.658C1.885 5.013 2.345 4 3.204 4h9.592a1 1 0 0 1 .753 1.659l-4.796 5.48a1 1 0 0 1-1.506 0z'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 0.5rem center;
    padding-right: 1.75rem;
}

.mms-filter__select:focus {
    border-color: var(--pc-accent);
    box-shadow: 0 0 0 2px var(--pc-accent-glow);
}

.mms-filter__select--type {
    flex: 0 0 70px;
}

.mms-filter__select--value {
    flex: 1;
    min-width: 0;
}

.mms-filter__select option {
    background: var(--pc-bg-secondary);
    color: var(--pc-text-primary);
}

@media (max-width: 767px) {
    .mms-filter__select {
        font-size: 0.85rem;
        padding: 0.3rem 0.5rem;
        padding-right: 1.5rem;
    }
    .mms-filter__select--type {
        flex: 0 0 60px;
    }
}
</style>
